import React, {createContext, useContext, useState} from 'react';
import {useIntl} from 'react-intl';
import Util from '../../../shared/util/util';
import {ESTIMATION_UNIT} from '../../../../constants';
import * as tracking from '../../../../tracking';

const TotalsContext = createContext(null);

/**
 * This is the Context Provider for the the Scoping page header totals.
 *
 * It is used for calculating the totals values, and for handling the expanding state of the box.
 *
 * @param children The
 * @param projectData The project data used for calculating.
 * @param projectSettings The settings used for showing the totals
 * @returns {JSX.Element} The Context Provider element.
 */
export const ProjectScopingTotalsProvider = ({children, projectData, settings}) => {
	const intl = useIntl();

	const TOTALS_EXPANDED_KEY = 'project-scoping-totals-expanded';

	const [totalsExpanded, setTotalsExpanded] = useState(JSON.parse(Util.localStorageGetItem(TOTALS_EXPANDED_KEY) || true));

	const startDate = Util.CreateNonUtcMomentDate(projectData.projectStartYear, projectData.projectStartMonth, projectData.projectStartDay);
	const endDate = Util.CreateNonUtcMomentDate(projectData.projectEndYear, projectData.projectEndMonth, projectData.projectEndDay);

	const startDateString = startDate ? `${intl.formatDate(startDate, {day: 'numeric'})} ${intl.formatDate(startDate, {month: 'short'})}, ${intl.formatDate(startDate, {year: 'numeric'})}` : intl.formatMessage({id: 'project_overview.no_start_date'});
	const endDateString = endDate ? `${intl.formatDate(endDate, {day: 'numeric'})} ${intl.formatDate(endDate, {month: 'short'})}, ${intl.formatDate(endDate, {year: 'numeric'})}` : intl.formatMessage({id: 'project_overview.no_end_date'});

	const progress = projectData.rolledUpData.projectProgress ? projectData.rolledUpData.projectProgress / 100.0 : 0;
	const plannedRevenue = projectData.rolledUpData.price;

	const actualRevenue = projectData.projectActualRevenue;
	const actualRevenueForTasks = projectData.rolledUpData.actualPrice ? Util.getFormattedCurrencyValue(settings.currencySymbol, intl.formatNumber(projectData.rolledUpData.actualPrice, {format: 'always_two_decimal'})) : '-';
	const actualRevenueForProject = projectData.projectRevenueNoTask ? Util.getFormattedCurrencyValue(settings.currencySymbol, intl.formatNumber(projectData.projectRevenueNoTask, {format: 'always_two_decimal'})) : '-';

	const isEstimatedInHours = projectData.rolledUpData.estimationUnit === ESTIMATION_UNIT.HOURS;
	const estimate = projectData.rolledUpData.estimate ? (isEstimatedInHours ? projectData.rolledUpData.estimate / 60 : projectData.rolledUpData.estimate) : 0;

	const timeEntries = projectData.projectTimeEntries ? projectData.projectTimeEntries / 60 : 0;
	const timeEntriesOnTasks = projectData.rolledUpData.timeRegistered ? Util.convertMinutesToFullHour(projectData.rolledUpData.timeRegistered, intl) : '-';
	const timeEntriesOnProject = projectData.projectTimeRegisteredNoTask ? Util.convertMinutesToFullHour(projectData.projectTimeRegisteredNoTask, intl) : '-';

	const remaining = projectData.rolledUpData.remaining ? (isEstimatedInHours ? projectData.rolledUpData.remaining / 60 : projectData.rolledUpData.remaining) : 0;

	const difference = projectData.projectDifferenceEstimate ? projectData.projectDifferenceEstimate / 60 : 0;

	const toggleTotalsExpanded = () => {
		setTotalsExpanded(value => {
			const newValue = !value;
			Util.localStorageSetItem(TOTALS_EXPANDED_KEY, newValue);
			tracking.trackElementClicked('Expand scoping totals', {enabled: newValue});
			return newValue;
		});
	}

	return (
		<TotalsContext.Provider value={
			{
				totalsExpanded, toggleTotalsExpanded,
				projectData, // The original project data
				settings, // The settings
				totalsData: {
					startDate, startDateString,
					endDate, endDateString,
					progress,
					plannedRevenue,
					actualRevenue,
					actualRevenueForTasks,
					actualRevenueForProject,
					isEstimatedInHours,
					estimate,
					timeEntries,
					timeEntriesOnTasks,
					timeEntriesOnProject,
					remaining,
					difference,
				}
			}
		}>
			{children}
		</TotalsContext.Provider>
	);
}

export const useProjectTotals = () => useContext(TotalsContext);




